import { difference } from "lodash-es";
import React, { useContext, useMemo } from "react";

import { ICustomerStatus } from "../../generated/types";
import { logNever } from "../../utils/assertNever";
import { CustomerContext } from "../customer/CustomerContext";
import { Permission } from "./Permission";

export interface IPermissionsContext {
    hasPermission(permission: Permission): boolean;
    hasAnyPermissions(permissions: Permission[]): boolean;
    hasAllPermissions(permissions: Permission[]): boolean;
    permissions: string[];
}

const DEFAULT: IPermissionsContext = {
    hasPermission: () => false,
    hasAnyPermissions: () => false,
    hasAllPermissions: () => false,
    permissions: [],
};

export const PermissionsContext = React.createContext<IPermissionsContext>(DEFAULT);

interface IProps {
    permissions: string[];
}

/**
 * Must be rendered within a CustomerContextProvider.
 */
export const PermissionsContextProvider: React.FC<React.PropsWithChildren<IProps>> = ({ permissions, children }) => {
    const isCustomerStatusOpen = useIsCustomerStatusOpen();
    const resolvedPermissions = useMemo((): string[] => {
        if (isCustomerStatusOpen) {
            return permissions;
        }

        return difference(permissions, CUSTOMER_STATUS_NOT_OPEN_EXCLUDED_PERMISSIONS);
    }, [isCustomerStatusOpen, permissions]);

    const activePermissionSet = useMemo((): Set<string> => new Set(resolvedPermissions), [resolvedPermissions]);

    const contextValue = useMemo(
        (): IPermissionsContext => ({
            hasPermission: (permission) => activePermissionSet.has(permission),
            hasAnyPermissions: (anyPermissions) =>
                anyPermissions.some((permission) => activePermissionSet.has(permission)),
            hasAllPermissions: (allPermissions) =>
                allPermissions.every((permission) => activePermissionSet.has(permission)),
            permissions: resolvedPermissions,
        }),
        [activePermissionSet, resolvedPermissions],
    );

    return <PermissionsContext.Provider value={contextValue}>{children}</PermissionsContext.Provider>;
};

function useIsCustomerStatusOpen(): boolean {
    const { selectedCustomer: customer } = useContext(CustomerContext);

    if (customer == null) {
        return false;
    }

    switch (customer.status) {
        case ICustomerStatus.PreKyc:
        case ICustomerStatus.Open:
            return true;
        case ICustomerStatus.Frozen:
        case ICustomerStatus.Closed:
            return false;
        default:
            return logNever(customer.status, false);
    }
}

// Permissions that are removed from the user if their customer status is not open.
const CUSTOMER_STATUS_NOT_OPEN_EXCLUDED_PERMISSIONS: Permission[] = [
    Permission.WriteAccounts,
    Permission.WriteBillsSelf,
    Permission.WriteBillsOrg,
    Permission.WriteCardSelf,
    Permission.WriteCardOrg,
    Permission.WriteCheckDepositCreate,
    Permission.WriteTransferInternal,
    Permission.WriteOrgAdmin,
    Permission.WriteConversionRequest,
    Permission.WriteConversionResolve,
    Permission.WriteConversionSelfApprove,
    Permission.WritePaymentRequest,
    Permission.WritePaymentBulkRequest,
    Permission.WritePaymentResolve,
    Permission.WritePaymentSelfApprove,
    Permission.WritePaymentDebit,
    Permission.RequestRecipientOrg,
    Permission.WriteRecipientBulkRequest,
    Permission.WriteRecipientApprove,
    Permission.WriteRecipientSelfApprove,
    Permission.WriteRecipientDisable,
];
